import { cur, locale } from '../lib/units';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Timer, CalendarPlus, BellRing, FileText } from 'lucide-react';
import { scaffoldPdf } from '../lib/scafPdf';
import { downloadIcs, enableScafNotify, notifyOn, scafCheck, icsItems } from '../lib/scafRemind';
import { uid } from '../lib/model';
import { scafState } from '../lib/inspect';
import { askConfirm } from '../lib/askConfirm';
import DateSel from './DateSel';

const eur = (v) => `${(v || 0).toLocaleString(locale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur()}`;
const dstr = (ms) => new Date(ms).toLocaleDateString();

function Row({ t, s, i, upd, del, onIcs }) {
  const r = scafState(s);
  return (
    <div className={`sc-row sc-${r.st}`} data-testid={`scaffold-item-${i}`}>
      <div className="row">
        <input className="sc-name" data-testid={`scaffold-name-${i}`} value={s.name || ''} placeholder={t.scName} onChange={(e) => upd({ name: e.target.value })} />
        {r.due && !s.end && <button className="icon-btn" data-testid={`scaffold-ics-${i}`} title={t.scIcs} onClick={onIcs}><CalendarPlus size={14} /></button>}
        <button className="icon-btn" data-testid={`scaffold-del-${i}`} onClick={del}><Trash2 size={14} /></button>
      </div>
      <div className="sw-grid">
        <label>{t.scStart}<DateSel testid={`scaffold-start-${i}`} value={s.start} onChange={(v) => upd({ start: v })} /></label>
        <label>{t.scWeeks}<input inputMode="numeric" data-testid={`scaffold-weeks-${i}`} value={s.weeks ?? ''} placeholder="4" onChange={(e) => upd({ weeks: e.target.value })} /></label>
        <label>{t.scPrice} ({cur()})<input inputMode="decimal" data-testid={`scaffold-price-${i}`} value={s.price ?? ''} placeholder="350" onChange={(e) => upd({ price: e.target.value.replace(',', '.') })} /></label>
        <label>{t.scEnd}<DateSel testid={`scaffold-end-${i}`} value={s.end} onChange={(v) => upd({ end: v })} /></label>
      </div>
      {r.st !== 'none' && (
        <p className="sc-status" data-testid={`scaffold-status-${i}`}>
          <b>{t['scSt_' + r.st]}</b> · {t.scDue} {dstr(r.due)} · {r.left >= 0 ? t.scLeft.replace('{n}', r.left) : t.scOver.replace('{n}', r.over)} · {t.scBase} {eur(r.base)}
          {r.extra > 0 && <> · <span className="sc-extra" data-testid={`scaffold-extra-${i}`}>{t.scExtra}: {r.extraW} × = {eur(r.extra)}</span></>}
        </p>
      )}
    </div>
  );
}

// scaffold rental timer with traffic light (green > 7 days, yellow ≤ 7 days, red overdue) and extra rent
export default function ScaffoldRent({ t, project, updateProject }) {
  const list = project.scaffolds || [];
  const warned = useRef(null);
  const [nOn, setNOn] = useState(notifyOn);
  useEffect(() => {
    if (warned.current === project.id) return;
    warned.current = project.id;
    scafCheck(t);
    const bad = list.map(scafState).filter((r) => r.st === 'red' || r.st === 'yellow');
    if (bad.length) toast.warning(`🪜 ${t.scWarn.replace('{n}', bad.length)}`, { duration: 8000 });
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (scaffolds) => updateProject((p) => ({ ...p, scaffolds }));
  const ics = (l) => { if (downloadIcs(t, l, project.title)) toast.success(t.scIcsDone); };
  const notify = async () => {
    const r = await enableScafNotify(t);
    if (r === 'no') toast.error(t.scNotifyNo); else if (r === 'denied') toast.error(t.scNotifyDenied); else { setNOn(true); toast.success(r === 'ok' ? t.scNotifyOn : t.scNotifyStart); }
  };
  return (
    <div className="fx-box sc-box" data-testid="scaffold-box">
      <b><Timer size={15} /> {t.scRentTitle} ({list.length})</b>
      {list.map((s, i) => <Row key={s.id} t={t} s={s} i={i} upd={(p) => set(list.map((x) => (x.id === s.id ? { ...x, ...p } : x)))} del={async () => (await askConfirm(t.scDelQ, { ok: t.pitDelete, cancel: t.btnCancel })) && set(list.filter((x) => x.id !== s.id))} onIcs={() => ics([s])} />)}
      <div className="row sc-remind">
        {list.length > 0 && <button className="btn btn-outline btn-sm" data-testid="scaffold-pdf-btn" onClick={() => scaffoldPdf(t, project)}><FileText size={14} /> {t.scRentPdf}</button>}
        {icsItems(list).length > 1 && <button className="btn btn-outline btn-sm" data-testid="scaffold-ics-all-btn" onClick={() => ics(list)}><CalendarPlus size={14} /> {t.scIcsAll}</button>}
        {list.length > 0 && (nOn ? <small className="muted" data-testid="scaffold-notify-on">🔔 {t.scNotifyActive}</small> : <button className="btn btn-outline btn-sm" data-testid="scaffold-notify-btn" onClick={notify}><BellRing size={14} /> {t.scNotify}</button>)}
      </div>
      <button className="btn btn-dark btn-sm" data-testid="scaffold-add-btn" onClick={() => set([...list, { id: uid('scf'), name: `${t.scName} ${list.length + 1}`, start: new Date().toISOString().slice(0, 10), weeks: '4', price: '' }])}><Plus size={14} /> {t.scAdd}</button>
    </div>
  );
}
